import {CompileError} from "./error";

/**
 * Result of processing Unicode escape characters, e.g. \U0000 -> (NUL). This occurs before any other processing.
 *
 * Please see the documentation for `replaceUnicodeEscapes` for more information on how these offsets are calculated.
 */
export class UnicodeUnescapedResult {
	constructor(
		readonly result: string,
		readonly replacements: {newOffset: number; oldOffset: number}[],
		readonly errors: CompileError[]
	) {}

	cachedLastReplacement: {
		validStart: number;
		validEnd: number;
		offset: number;
	} | null = null;

	postEscapeToPre(offset: number) {
		const {replacements} = this;

		// Binary search for last replacement where the newOffset is smaller than the given offset,
		// or null if that replacement doesn't exist

		if (replacements.length === 0) return offset;
		if (this.cachedLastReplacement) {
			// we're likely to keep querying in the same region, so do this
			const {
				validStart,
				validEnd,
				offset: cachedOffset,
			} = this.cachedLastReplacement;
			if (validStart <= offset && offset < validEnd)
				return offset + cachedOffset;
		}

		let low = 0;
		let high = replacements.length - 1;

		// Binary search for the first replacement where newOffset <= offset
		while (low < high) {
			const mid = (low + high + 1) >> 1;
			if (replacements[mid]!.newOffset <= offset) {
				low = mid;
			} else {
				high = mid - 1;
			}
		}

		const replacement = replacements[low]!;
		if (replacement.newOffset <= offset) {
			const diff = replacement.oldOffset - replacement.newOffset;
			this.cachedLastReplacement = {
				validStart: replacement.newOffset,
				validEnd:
					replacements.at(low + 1)?.newOffset ?? 1 << 30 /* ~infinity */,
				offset: diff,
			};
			return offset + diff;
		} else {
			return offset;
		}
	}

	// Convert a post-escape span into pre-escape span. Used right after lexing is finished.
	fixupSpanInPlace(span: {start: number; end: number}) {
		span.start = this.postEscapeToPre(span.start);
		span.end = this.postEscapeToPre(span.end);
	}
}

/**
 * Example: In the string "\u0041@\u0042\u0043&&\u0044", which unescapes to "A@BC&&D", we have the following
 * replacement table:
 *
 * -> pre escape index
 *  0 1 2 3 4 5 6 7 8 9 10 11 12 13 14 15 16 17 18 19 20 21 22 23 24 25 26
 *  \ u 0 0 4 1 @ \ u 0 0  4  2  \  u  0  0  4  3  &  &  \  u  0  0  4  4
 *     ┌────────┘                │                 │
 *     │     ┌───────────────────┘                 │
 *     │     │     ┌───────────────────────────────┘
 *     │     │     │
 *  A  @  B  C  &  &  D
 *  0  1  2  3  4  5  6
 *
 * Hence the offset map is [ 1 -> 6, 3 -> 13, 4 -> 19 ]. If we want to find the pre-escape location of a post-escape
 * character at index j, we look for the first replacement where pre <= j, and return j + (pre - post). For example,
 * consider the second & above. The first mapping where pre <= 5 is the map 4 -> 19, so we return 5 + (19 - 4) = 20.
 */
export function replaceUnicodeEscapes(text: string): UnicodeUnescapedResult {
	let offset: number;
	let totalOffset = 0; // offset in original source
	let result = "";
	const originalText = text;

	const errors: UnicodeUnescapedResult["errors"] = [];
	const replacements: UnicodeUnescapedResult["replacements"] = [];

	function advance(count: number, addReplacement: boolean) {
		text = text.slice(count);
		totalOffset += count;

		// In post-escape coordinates, the character following the replacement is result.length,
		// and this character should be mapped to the new value of totalOffset.
		if (addReplacement) {
			replacements.push({
				newOffset: result.length + 1,
				oldOffset: totalOffset,
			});
		}
	}

	let atLeastOneReplacement = false;
	outer: while ((offset = text.indexOf("\\u")) !== -1) {
		offset = offset!;

		// Even # of backslashes before the u -> treat literally, don't subst
		// (https://docs.oracle.com/javase/specs/jls/se8/html/jls-3.html#jls-3.3)
		let backslashes = 0;
		while (offset >= backslashes && text[offset - backslashes] === "\\")
			++backslashes;
		if (backslashes % 2 === 0) {
			result += text.slice(0, offset + 2 /* include \u */);
			advance(offset + 2, true);
			continue;
		}

		atLeastOneReplacement = true;

		let digitStart = offset + 1;
		while (text[digitStart] === "u")
			// multiple u allowed, e.g. \uu0000 -> \u0000. (Why??)
			++digitStart;
		const expectedHex = text.slice(digitStart, digitStart + 4);
		if (expectedHex.length !== 4) {
			errors.push(
				new CompileError("Unfinished Unicode escape character", {
					start: totalOffset + offset,
					end: totalOffset + digitStart + 4,
				})
			);
			break; // only happens at EOF
		}

		let val = 0;
		for (let i = 0; i < expectedHex.length; ++i) {
			const c = expectedHex[i]!;
			const digit = Number.parseInt(c, 16);
			if (Number.isNaN(digit)) {
				errors.push(
					new CompileError(
						"Invalid character in Unicode escape (require: 0-9, a-f, A-F; got: " +
							c +
							")",
						{
							start: totalOffset + digitStart,
							end: totalOffset + digitStart + i + 1,
						}
					)
				);
				result += text.slice(0, offset);
				advance(digitStart + 4, true);
				continue outer;
			}

			val = val * 16 + digit;
		}

		const valAsChar = String.fromCharCode(val);
		if (offset !== -1)
			// if not continuing from a backslash
			result += text.slice(0, offset);

		// common case: done with this Unicode escape
		advance(digitStart + 4, true);
		result += valAsChar;
	}

	return new UnicodeUnescapedResult(
		atLeastOneReplacement ? result + text : originalText,
		replacements,
		errors
	);
}
